import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Twitter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  
  const companyEmail = 'contact@limitless-solutions.pl';
  const companyPhone = '+48 600 037 207';

  const navigation = [
    { nameKey: 'nav.home', href: '/#home' },
    { nameKey: 'nav.about', href: '/#about' },
    { nameKey: 'nav.services', href: '/#services' },
    { nameKey: 'nav.contact', href: '/#contact' },
    { nameKey: 'nav.blog', href: '/blog' },
  ];

  return (
    <motion.footer
      className="bg-navy text-white py-16 relative overflow-hidden"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      {/* Animated background shapes */}
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.3, 0.5] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 left-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 6, repeat: Infinity }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.img
              src="https://customer-assets.emergentagent.com/job_glass-portfolio-27/artifacts/a8c3zkhn_logo_full.png"
              alt="Limitless Solutions Logo"
              className="h-12 w-auto mb-6"
              whileHover={{ scale: 1.05 }}
            />
            <p className="text-white/60 font-light text-sm leading-relaxed max-w-sm mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-4">
              {[
                { icon: Linkedin, href: '#' },
                { icon: Twitter, href: '#' },
              ].map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  className="w-10 h-10 flex items-center justify-center rounded-sm bg-white/5 hover:bg-gold/20 transition-colors duration-300"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <social.icon className="w-4 h-4 text-white/70" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="text-xs font-medium text-gold tracking-[0.2em] uppercase mb-6">
              {t('footer.navigation')}
            </h4>
            <ul className="space-y-3">
              {navigation.map((item, index) => (
                <motion.li
                  key={item.nameKey}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <motion.a
                    href={item.href}
                    className="text-white/60 font-light text-sm hover:text-gold transition-colors duration-300 inline-block"
                    whileHover={{ x: 5 }}
                  >
                    {t(item.nameKey)}
                  </motion.a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="text-xs font-medium text-gold tracking-[0.2em] uppercase mb-6">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3">
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <motion.a
                  href={`mailto:${companyEmail}`}
                  className="text-white/60 font-light text-sm hover:text-gold transition-colors duration-300 inline-block"
                  whileHover={{ x: 5 }}
                >
                  {companyEmail}
                </motion.a>
              </motion.li>
              <motion.li
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <motion.a
                  href={`tel:${companyPhone.replace(/\s/g, '')}`}
                  className="text-white/60 font-light text-sm hover:text-gold transition-colors duration-300 inline-block"
                  whileHover={{ x: 5 }}
                >
                  {companyPhone}
                </motion.a>
              </motion.li>
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <motion.div
          className="border-t border-white/10 pt-8"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <motion.p
              className="text-white/40 font-light text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              © {currentYear} <Link to="/blog/admin" className="hover:text-gold transition-colors">Limitless Solutions</Link>. {t('footer.rights')}
            </motion.p>
            <motion.p
              className="text-white/40 font-light text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              limitless-solutions.pl
            </motion.p>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;
